import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Modal } from '@feathr/components';

import type { TChannel, TCreativeType } from '../types';
import type { TAddCreativeStep } from './AddCreativeFlow.hooks';
import { useAddCreativeFlow } from './AddCreativeFlow.hooks';
import ChooseChannelContent from './ChooseChannelContent';
import ChooseCreativeTypeContent from './ChooseCreativeTypeContent';

import * as styles from './AddCreativeFlow.css';

export interface IAddCreativeFlowProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Callback when flow completes with selected channel and creative type */
  onComplete: (channel: TChannel, creativeType: TCreativeType) => void;
  /** Initial step to show (defaults to 'channel') */
  initialStep?: TAddCreativeStep;
  /** Initial channel selection (used with initialStep='creative-type') */
  initialChannel?: TChannel;
  /** Available channels based on campaign selection. If single channel, auto-selects and skips step 1. */
  availableChannels?: TChannel[];
}

function AddCreativeFlow({
  isOpen,
  onClose,
  onComplete,
  initialStep,
  initialChannel,
  availableChannels,
}: Readonly<IAddCreativeFlowProps>): JSX.Element {
  const { t } = useTranslation();
  const {
    step,
    selectedChannel,
    selectedCreativeType,
    handleChannelSelect,
    handleCreativeTypeSelect,
    handleNextFromChannel,
    handleNextFromCreativeType,
    handleBack,
    handleClose,
    isNextDisabledOnChannel,
    isNextDisabledOnCreativeType,
    modalTitle,
    modalDescription,
    creativeTypeOptions,
    shouldHideBackButton,
  } = useAddCreativeFlow({ onClose, onComplete, initialStep, initialChannel, availableChannels });

  const channelStepActions = [
    <Button key={'cancel'} name={'cancel'} onClick={handleClose}>
      {t('Cancel')}
    </Button>,
    <Button
      disabled={isNextDisabledOnChannel}
      key={'next'}
      name={'next'}
      onClick={handleNextFromChannel}
      type={'primary'}
    >
      {t('Next')}
    </Button>,
  ];

  const creativeTypeStepActions = [
    <Button key={'cancel'} name={'cancel'} onClick={handleClose}>
      {t('Cancel')}
    </Button>,
    <Button
      disabled={isNextDisabledOnCreativeType}
      key={'next'}
      name={'next'}
      onClick={handleNextFromCreativeType}
      type={'primary'}
    >
      {t('Next')}
    </Button>,
  ];

  const leftActions =
    step === 'creative-type' && !shouldHideBackButton ? (
      <Button key={'back'} name={'back'} onClick={handleBack}>
        {t('Back')}
      </Button>
    ) : undefined;

  const rightActions = step === 'channel' ? channelStepActions : creativeTypeStepActions;

  return (
    <Modal
      description={modalDescription}
      leftActions={leftActions}
      onClose={handleClose}
      opened={isOpen}
      rightActions={rightActions}
      title={modalTitle}
    >
      <div className={styles.container}>
        {step === 'channel' && (
          <ChooseChannelContent
            availableChannels={availableChannels}
            onChannelSelect={handleChannelSelect}
            selectedChannel={selectedChannel}
          />
        )}
        {step === 'creative-type' && selectedChannel && (
          <ChooseCreativeTypeContent
            onCreativeTypeSelect={handleCreativeTypeSelect}
            options={creativeTypeOptions}
            selectedCreativeType={selectedCreativeType}
          />
        )}
      </div>
    </Modal>
  );
}

export default AddCreativeFlow;
